<template>
  <div
    v-loading="loading"
    class="account-detail"
  >
    <BHeightAdjuster>
      <template v-if="account">
        <ElHeader class="header">
          <BIcon
            class="back-to-lead"
            outlined
            size="large"
            icon-weight="light"
            :disabled="!canGoBack"
            @click="backToLead"
          >
            arrow_circle_left
          </BIcon>
        </ElHeader>
        <ElContainer>
          <ElAside class="company-info">
            <div class="company-info-header">
              <h3>{{ account.companyName }}</h3>
            </div>
            <div class="company-info-body" />
          </ElAside>
          <ElContainer>
            <ElMain class="main-content">
              <BTabs
                class="tabs"
                :label-items="mainTabLabelItems"
              />
              <AccountHistoriesFilter
                v-model:call-target-id="filteredCallTargetId"
                v-model:history-type="historyType"
                :call-targets="account.callTargets"
              />
              <AccountHistories
                v-model:picked-call-target-id="pickedCallTargetId"
                :loading="historiesLoading"
                :load-more="loadMoreHistories"
                :histories="(loadedHistories as AccountHistory[])"
                :scroll-disabled="loadedHistoriesScrollDisabled"
              />
            </ElMain>
            <ElAside class="lead-list">
              <div class="lead-list-header">
                <h3>{{ $t('callTarget.title') }}</h3>
              </div>
              <div class="lead-list-body">
                <AccountLeads
                  v-model:picked-call-target-id="pickedCallTargetId"
                  :call-targets="account?.callTargets || []"
                  :attribute-keys="leadAttributeKeys"
                />
              </div>
            </ElAside>
          </ElContainer>
        </ElContainer>
      </template>
    </BHeightAdjuster>
  </div>
</template>

<script setup lang="ts">
import { isEqual } from 'lodash';
import { computed, onBeforeMount, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { type LocationQuery, useRoute, useRouter } from 'vue-router';
import type { Account, AccountHistory, HistoryType } from '@/api/openapi/models';
import { AccountApiService } from '@/api/user/resources/account';
import AccountHistories from '@/components/organisms/user/account/AccountHistories.vue';
import AccountHistoriesFilter from '@/components/organisms/user/account/AccountHistoriesFilter.vue';
import AccountLeads from '@/components/organisms/user/account/AccountLeads.vue';
import { useInfiniteScroll } from '@/composable/infinite-loading';
import { usePrevRoute } from '@/composable/user/prev-route';
import { useWait } from '@/composable/vue-wait';
import AccountDetail from './AccountDetail.vue';

type THistoryFilterQuery = {
  filteredCallTargetId: number | undefined;
  historyType: HistoryType | undefined;
};
type TRouteQuery = THistoryFilterQuery & {
  pickedCallTargetId: number | undefined;
};

const filteredCallTargetId = ref<number | undefined>();
const historyType = ref<HistoryType | undefined>();
const pickedCallTargetId = ref<number | undefined>();
const companyName = computed(() => decodeURIComponent(route.params.key as string));

const route = useRoute();
const { doActionWithWait, wait } = useWait();
const i18n = useI18n();
const router = useRouter();
const accountApi = new AccountApiService();

const { goBack: backToLead, setPrevRoute, canGoBack } = usePrevRoute();
defineExpose({ setPrevRoute });
defineOptions({
  beforeRouteEnter(_to, from, next) {
    next((vm: InstanceType<typeof AccountDetail>) => {
      vm.setPrevRoute(from);
    });
  },
});

const setRouteQuery = async (routeQuery: TRouteQuery, toClearHistoryItem = false) => {
  const newQuery = {
    filteredCallTargetId: routeQuery.filteredCallTargetId?.toString(),
    historyType: routeQuery.historyType,
    pickedCallTargetId: routeQuery.pickedCallTargetId?.toString(),
  };
  const prevQuery = {
    filteredCallTargetId: route.query.filteredCallTargetId,
    historyType: route.query.historyType,
    pickedCallTargetId: route.query.pickedCallTargetId,
  };
  if (isEqual(newQuery, prevQuery)) return;

  const historyItemQuery = toClearHistoryItem ? { historyItem: undefined } : {};

  await router.replace({
    ...route,
    query: {
      ...route.query,
      ...newQuery,
      ...historyItemQuery,
    },
  });
};


// --- account

const account = ref<Account | undefined>();

const fetchAccount = async () => {
  await doActionWithWait('fetchAccount', async () => {
    const response = await accountApi.getAccount({
      request: { key: companyName.value },
    });
    account.value = response.data.account;
  });
};

// --- histories

// NOTE: 現状タブは見せかけ
const mainTabLabelItems = [
  { label: i18n.t('callHistory.title'), slotName: 'history' },
];

const histories = ref<History[]>([]);

const historiesLoading = computed(() => {
  return wait.is(['fetchHistories', 'loadAllHistories']);
});

const { loadedItems: loadedHistories, loadMore: loadMoreHistories, loadAll: loadAllHistories, reset: resetLoadedHistories, scrollDisabled: loadedHistoriesScrollDisabled } = useInfiniteScroll(() => histories.value, 20);

const setHistoriesFilterByRouteQuery = (query: LocationQuery) => {
  filteredCallTargetId.value = query.filteredCallTargetId ? Number(query.filteredCallTargetId) : undefined;
  historyType.value = query.historyType ? query.historyType as HistoryType : undefined;
};

const fetchHistories = async (query: THistoryFilterQuery) => {
  await doActionWithWait('fetchHistories', async () => {
    const response = await accountApi.getAccountHistories({
      request: {
        key: companyName.value,
        callTargetId: query.filteredCallTargetId || undefined,
        historyType: query.historyType || undefined,
      },
    });
    histories.value = response.data.histories;
  });
};
watch(() => histories.value, async () => {
  resetLoadedHistories();
  
  // 前回の処理が完了するまで待機
  await nextTick();
  
  if (route.query.historyItem) {
    // UUIDがある場合は全件表示
    await doActionWithWait('loadAllHistories', async () => {
      await loadAllHistories();
    });
  } else {
    // それ以外は最初1回分のみ呼ぶ
    await loadMoreHistories();
  }
});

// --- lead list

// NOTE: 現状固定
const leadAttributeKeys = [
  'callTargetInfo.email',
  'callTargetInfo.personalTel',
  'callTargetInfo.department',
  'callTargetInfo.position',
];

// --- general

const setPickedCallTargetIdByRouteQuery = (query: LocationQuery) => {
  pickedCallTargetId.value = query.pickedCallTargetId ? Number(query.pickedCallTargetId) : undefined;
};
watch(() => pickedCallTargetId.value, async (newValue) => {
  await setRouteQuery({
    filteredCallTargetId: filteredCallTargetId.value,
    historyType: historyType.value,
    pickedCallTargetId: newValue,
  });
});

onBeforeMount(async () => {
  // URLから各種値を取得
  setPickedCallTargetIdByRouteQuery(route.query);
  setHistoriesFilterByRouteQuery(route.query);
  // データ取得
  await fetchAccount();
  await fetchHistories({
    filteredCallTargetId: filteredCallTargetId.value,
    historyType: historyType.value,
  });

  // フィルター変更時の処理を設定
  watch<THistoryFilterQuery>(() => ({ filteredCallTargetId: filteredCallTargetId.value, historyType: historyType.value }), async (newValue) => {
    const query = {
      filteredCallTargetId: newValue.filteredCallTargetId,
      historyType: newValue.historyType,
      pickedCallTargetId: pickedCallTargetId.value,
    };
    // NOTE: フィルタ保存しつつ、ヒストリーの選択状態を解除
    await setRouteQuery(query, true);
    await fetchHistories(query);
  });
});

const loading = computed(() => {
  return wait.is('fetchAccount');
});
</script>

<style lang="scss" scoped>
.account-detail {
  @include m-fixed-height(100vh);
  position: relative;
  width: 100%;
  overflow: hidden;

  :deep(.b-height-adjuster) {
    width: 100%;
    overflow: hidden;
  }

  :deep(.el-container) {
    height: 100%;
  }

  .header {
    @include m-flex-align-center;
    @include m-fixed-height($header-height);
    border-bottom: 1px solid $bdcolor-light;
    padding: $basespace-100 $basespace-500;

    .back-to-lead {
      cursor: pointer;
    }
  }
  .company-info {
    @include m-fixed-width(300px);
    height: $page-height;
    border-right: 1px solid $bdcolor-light;
    padding: $basespace-400 $basespace-500;
    overflow-y: auto;

    &-header {
      padding-bottom: $basespace-300;
      border-bottom: 1px solid $bdcolor-light;
    }
    &-body {
      padding: $basespace-300 0;
    }
  }
  .main-content {
    height: $page-height;
    padding: $basespace-200 $basespace-500;
    background-color: $bgcolor-base;
    overflow-y: auto;

    :deep(.el-tabs__header) {
      margin-bottom: $basespace-500;
    }

    .account-histories-filter {
      margin-bottom: $basespace-500;
    }
    :deep(.el-loading-spinner) {
      margin-top: $basespace-500;
    }
  }
  .lead-list {
    @include m-fixed-width(300px);
    height: $page-height;
    padding: $basespace-400 $basespace-500;
    border-left: 1px solid $bdcolor-light;
    background-color: $bgcolor-base;
    overflow-y: auto;

    &-header {
      padding-bottom: $basespace-100;
    }
  }
}
</style>
